/**
 * TimeAgo — Prompt 38 Part 2.1.
 *
 * Renders a live "Updated 5s ago" pill that re-tickers every second.
 * The backend already returns `fetchedAt` on coin/price endpoints; this
 * component just surfaces it, with color-coded freshness so users can see
 * at a glance whether the displayed price is current.
 *
 * Usage:
 *   <TimeAgo timestamp={data.fetchedAt} />
 *   <TimeAgo timestamp={data.fetchedAt} prefix="Quote from" />
 */

import { useEffect, useState } from 'react';

function formatAgo(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 1) return 'just now';
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Color thresholds:
 *   < 30s  → green  (fresh)
 *   < 120s → gray   (recent)
 *   ≥ 120s → amber  (stale)
 */
function freshnessClass(seconds) {
  if (seconds < 30) return 'text-emerald-400';
  if (seconds < 120) return 'text-gray-400';
  return 'text-amber-400';
}

export default function TimeAgo({
  timestamp,
  prefix = 'Updated',
  refreshIntervalMs = 1000,
  className = '',
  showDot = true,
}) {
  // Force a re-render every refreshIntervalMs so the displayed delta keeps moving
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!timestamp) return undefined;
    const id = setInterval(() => setTick(t => t + 1), refreshIntervalMs);
    return () => clearInterval(id);
  }, [timestamp, refreshIntervalMs]);

  if (!timestamp) return null;

  const seconds = (Date.now() - new Date(timestamp).getTime()) / 1000;
  const tone = freshnessClass(seconds);

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] ${tone} ${className}`}
      title={new Date(timestamp).toLocaleString()}
    >
      {showDot && (
        <span className={`w-1.5 h-1.5 rounded-full ${seconds < 30 ? 'bg-emerald-400 animate-pulse' : seconds < 120 ? 'bg-gray-500' : 'bg-amber-400'}`} />
      )}
      <span>{prefix} {formatAgo(seconds)}</span>
    </span>
  );
}

export { TimeAgo, formatAgo };
